import { Form, Input, DatePicker, Typography, Row, Col, Space, message, Segmented, Button, Flex, Divider } from "antd";
import { ArrowLeftOutlined, WarningOutlined } from "@ant-design/icons";
import Container from "../components/Container";
import LogoImage from "../components/LogoImage";
import ConfirmButton from "../components/ConfirmButton";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useGetUsimOrdersQuery, useUpdateActivationDateMutation } from "../api/apiSlice";
import styled from "styled-components";
import { UserActivationItem, UsimOrderItem } from "@/schemas/UsimOrdersResponse";
import { colors } from "../style";
import { loadLocalUsimData, saveLocalUsimData } from "../utils/localStorage";
import { CopyOutlined } from "@ant-design/icons";
import { ArrivalDateForm } from "./ArrivalDateForm";
import UsimStatusView from "../components/UsimStatusView";
const { Text } = Typography;

const ToggleWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  margin-bottom: 8px;
  background-color: #f5f5f5;
  border-radius: 8px;
  padding: 2px;
  max-width: 100%;
`;

const MobileLabel = styled(Text)`
  display: block;
  font-weight: bold;
  color: ${colors.primary};
  margin-bottom: 4px;
  @media (min-width: 576px) {
    display: none;
  }
`;

const DesktopHeaderRow = styled(Row)`
  display: none;
  @media (min-width: 576px) {
    display: flex;
  }
`;

const ProductRow = styled(Row)`
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
  @media (min-width: 576px) {
    margin-bottom: 8px;
    padding-bottom: 0;
    border-bottom: none;
  }
`;

export default function UsimActivation() {
  const { orderNo: orderNoParam } = useParams();
  const initialOrderNo = (orderNoParam as string) || "";
  const navigate = useNavigate()
  const [orderNo, setOrderNo] = useState<string>(initialOrderNo);
  const [tempOrderNo, setTempOrderNo] = useState<string>("");
  const [isOrderLoaded, setIsOrderLoaded] = useState<boolean>(false);
  const [isPendingActivation, setIsPendingActivation] = useState<boolean>(false);
  const [products, setProducts] = useState<UsimOrderItem[]>([]);
  const [showInput, setShowInput] = useState<boolean>(!initialOrderNo);
  const [cid, setCid] = useState<string>("");
  const [orderId, setOrderId] = useState<string>("");
  const [activeView, setActiveView] = useState<'arrival' | 'status'>('arrival');
  const [orderData, setOrderData] = useState<any>(null);
  const [showLoading, setShowLoading] = useState<boolean>(false);
  const [isAustraliaSim, setIsAustraliaSim] = useState<boolean>(false);
  // Control when to make API call - Default to TRUE to check local storage first
  const [skipApiCall, setSkipApiCall] = useState<boolean>(false);

  // RTK Query hook - Skip if no orderNo OR if we should skip (because we have local data)
  const { data: apiData, error: apiError, isLoading, refetch } = useGetUsimOrdersQuery(
    { orderNo },
    { skip: skipApiCall || !orderNo }
  );



  // Handle API response
  useEffect(() => {
    if (apiData) {

      setOrderData(apiData);
      saveLocalUsimData(apiData);
      checkAustraliaOrder(apiData.productDetails);
      setSkipApiCall(true); // Stop fetching after successful load
    }
  }, [apiData]);

  function checkAustraliaOrder(data: UsimOrderItem[]){
    console.log("data for checking", data);
    const isAustralia = data.some((item: UsimOrderItem) => item.type?.toLowerCase().includes("au"));
    console.log("isAustralia", isAustralia);
    setIsAustraliaSim(isAustralia);
  }

  // Fetch usim status data for joytel fields
  // const { data: statusData, isSuccess: statusSuccess } = useGetUsimUsageQuery(
  //   { cid, orderId: orderId },
  //   { skip: !cid || !orderId }
  // );

  // Handle refresh button
  function handleRefresh() {
    setShowLoading(true);
    localStorage.removeItem("usimLocalData");
    setOrderData(null);
    setSkipApiCall(false); // Enable API call
    setTimeout(() => refetch(), 0); // Ensure refetch triggers
    setShowLoading(false);
  }

  useEffect(() => {
    if (apiError) {
      setTempOrderNo(orderNo);
      return;
    }
    if (orderData) {
      setShowInput(false);
      setIsOrderLoaded(true);
      setProducts(orderData.productDetails || [])
      // check if the order has the activation date pending or not
      const hasPendingActivation = orderData.productDetails.some((product: any) =>
        product.activationDate === "" || product.activationDate == null
      );
      // console.log("has pendingactivatino", hasPendingActivation)
      setIsPendingActivation(hasPendingActivation);
    }
  }, [orderData, apiError]);

  function handleBack() {
    setShowInput(true);
    setOrderNo("");
    setIsOrderLoaded(false);
    setProducts([]);
    setTempOrderNo("");
    setSkipApiCall(true); // Reset skip state
  }

  function disabledPastDates(current: Dayjs) {
    return !!current && current.isBefore(dayjs().startOf("day"));
  }

  function handleActivationDateChange() {
  }

  return (
    <Container>
      <LogoImage />
      {!apiError && isOrderLoaded ? (
        <div
          style={{
            borderRadius: 16,
            padding: 24,
            width: "100%",
          }}
        >
          <div style={{ backgroundColor: "#ffffff", borderRadius: 16, marginBottom: 12, padding: "16px 16px 8px 16px" }}>
            <Row align="middle" justify="space-between" style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center" }}>
                <ArrowLeftOutlined onClick={handleBack} style={{ cursor: "pointer", marginRight: 8, fontSize: 16 }} />

                <Text strong style={{ fontSize: 16 }} copyable={{ text: orderNo, icon: <CopyOutlined style={{ color: colors.black }} /> }} >Order Number: {orderNo}</Text>
              </div>
              <Button type="default" size="small" onClick={handleRefresh}>
                Refresh
              </Button>
            </Row>
            <ToggleWrapper>
              <Segmented
                options={[
                  { label: 'Arrival Date', value: 'arrival', disabled: false },
                  { label: 'Status', value: 'status' }
                ]}
                value={activeView}
                onChange={(value) => setActiveView(value as 'arrival' | 'status')}
                block
                style={{ width: '100%' }}
              />
            </ToggleWrapper>
          </div>

          {activeView === 'arrival' && (
            <ArrivalDateForm
              products={products}
              handleActivationDateChange={handleActivationDateChange}
              disabledPastDates={disabledPastDates}
              orderNo={orderNo}
            />
          )}
          {activeView === 'status' && (
            <UsimStatusView
              products={products}
              orderNo={orderNo}
              onRefresh={handleRefresh}
            />
          )}
        </div>
      ) : (
        <div
          style={{
            backgroundColor: "#ffffff",
            borderRadius: 16,
            padding: 24,
            width: "100%",
          }}
        >
          {apiError && (
            <div>
              <Text type="danger">Order not found !! </Text>
              <Text>Please check order Number or retry</Text>
            </div>
          )}

          <Form style={{ width: "100%" }}>
            <Form.Item rules={[{ required: true, message: "Please enter your Order Number" }]}>
              <Input
                size="large"
                value={tempOrderNo}
                placeholder="Order Number"
                onChange={(e) => setTempOrderNo(e.target.value)}
                style={{
                  backgroundColor: "#fff",
                  color: "#111111",
                  borderColor: "#111",
                  marginTop: 16,
                  textTransform: "none",
                  fontFamily: "Arial, sans-serif",
                  fontWeight: "bold"
                }}
              />
            </Form.Item>
            <ConfirmButton
              onClick={() => {
                if (!tempOrderNo) {
                  message.warning("Please enter your Order Number");
                  return;
                }
                setSkipApiCall(false);
                window.location.href = `/status/${tempOrderNo.trim()}`;
                // setOrderNo(tempOrderNo.trim());
              }}
            />
          </Form>
        </div>
      )}
    </Container>
  );
}
